import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type KeyboardEvent } from 'react';
import { Badge, Button, Card, Spinner, ConfirmDialog } from '@/components/admin/ui';
import {
  DeviceLink,
} from '@/components';
import { DeviceTestPushPanel } from '@/components/admin/DeviceTestPushPanel';
import { UserVillagePanel } from '@/components/admin/UserVillagePanel';
import { formatAdminDateTime, formatInt } from '@/lib/format';
import { apiJson } from '@/lib/api';

type UserItem = {
  id: string;
  created_at: string;
  updated_at: string;
  display_name: string | null;
  email: string | null;
  clerk_id: string | null;
  deleted_at: string | null;
  device_count: number;
  subscribed: boolean;
  subscription_override: boolean;
};

type DeviceItem = {
  installation_id: string;
  nickname: string | null;
  platform: string;
  first_seen_at: string;
  last_seen_at: string;
  unsubscribed_at: string | null;
  app_version: string | null;
  build_number: string | null;
  os_version: string | null;
  locale: string | null;
  timezone: string | null;
  device_model: string | null;
  brand: string | null;
  hardware_id: string | null;
  system_name: string | null;
  total_memory_mb: number | null;
  cpu_abis: string | null;
  total_disk_mb: number | null;
  screen: string | null;
  is_tablet: boolean | null;
  is_emulator: boolean | null;
  api_level: number | null;
};

type UserStreak = {
  current_streak: number;
  longest_streak: number;
  qualified_today: boolean;
  freezes_remaining: number;
  last_qualified_local_date: string | null;
  timezone: string;
};

type Detail = {
  user: UserItem;
  devices: DeviceItem[];
  stats: { flashcards_swiped: number; game_runs_played: number };
  streak: UserStreak | null;
};

type UserFeatureFlagItem = {
  key: string;
  enabled: boolean;
  allowlisted: boolean;
};

type UserFeatureFlagsResponse = {
  items: UserFeatureFlagItem[];
};

const USER_DETAIL_TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'subscription', label: 'Subscription' },
  { id: 'flags', label: 'Feature flags' },
  { id: 'streak', label: 'Streak' },
  { id: 'activity', label: 'Activity' },
  { id: 'village', label: 'Hmong Village' },
  { id: 'devices', label: 'Devices' },
  { id: 'push', label: 'Test push' },
] as const;

type UserDetailTab = (typeof USER_DETAIL_TABS)[number]['id'];

export const Route = createFileRoute('/admin/users/$userId')({
  component: UserDetailPage,
});

type PendingOverride = { next: boolean; title: string; message: string };
type PendingUserFlag = {
  key: string;
  next: boolean;
  title: string;
  message: string;
};

type DeviceDetailRow = { label: string; value: string };

function deviceDetailRows(d: DeviceItem): DeviceDetailRow[] {
  const candidates: Array<[string, string | null]> = [
    ['First seen', formatAdminDateTime(d.first_seen_at)],
    ['Last seen', formatAdminDateTime(d.last_seen_at)],
    ['App version', d.app_version],
    ['Build', d.build_number],
    ['OS version', d.os_version],
    ['System', d.system_name],
    ['Device model', d.device_model],
    ['Brand', d.brand],
    ['Hardware ID', d.hardware_id],
    ['Locale', d.locale],
    ['Timezone', d.timezone],
    ['Screen', d.screen],
    ['CPU ABIs', d.cpu_abis],
    ['RAM', d.total_memory_mb != null ? `${formatInt(d.total_memory_mb)} MB` : null],
    ['Disk', d.total_disk_mb != null ? `${formatInt(d.total_disk_mb)} MB` : null],
    ['Tablet', d.is_tablet == null ? null : d.is_tablet ? 'Yes' : 'No'],
    ['Emulator', d.is_emulator == null ? null : d.is_emulator ? 'Yes' : 'No'],
    ['API level', d.api_level != null ? formatInt(d.api_level) : null],
    ['Notifications off at', d.unsubscribed_at ? formatAdminDateTime(d.unsubscribed_at) : null],
  ];
  return candidates
    .filter((row): row is [string, string] => row[1] != null && row[1].trim() !== '')
    .map(([label, value]) => ({ label, value }));
}

function DeviceDetailsCard({ device: d }: { device: DeviceItem }) {
  return (
    <Card padding="sm">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <DeviceLink
            installationId={d.installation_id}
            nickname={d.nickname}
            label="auto"
            className="text-sm font-medium"
          />
          <Badge variant="neutral">{d.platform}</Badge>
          {d.unsubscribed_at != null ? <Badge variant="warning">Notifications off</Badge> : null}
        </div>
        <DeviceLink
          installationId={d.installation_id}
          label="short"
          className="text-xs text-nova-muted-foreground"
        />
      </div>
      <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <dt className="text-xs text-nova-muted-foreground">Installation</dt>
          <dd className="break-all font-mono text-xs">{d.installation_id}</dd>
        </div>
        {deviceDetailRows(d).map((row) => (
          <div key={row.label}>
            <dt className="text-xs text-nova-muted-foreground">{row.label}</dt>
            <dd className="break-words">{row.value}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

export function UserDetailPage() {
  const { userId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<UserDetailTab>('profile');
  const [pendingOverride, setPendingOverride] = useState<PendingOverride | null>(
    null,
  );
  const [pendingFlag, setPendingFlag] = useState<PendingUserFlag | null>(null);
  const [confirmClearStreak, setConfirmClearStreak] = useState(false);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'user', userId],
    queryFn: () =>
      apiJson<Detail>(`/v1/admin/users/${encodeURIComponent(userId)}`),
  });

  const overrideMutation = useMutation({
    mutationFn: (subscription_override: boolean) =>
      apiJson<UserItem>(`/v1/admin/users/${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ subscription_override }),
      }),
    onSuccess: () => {
      setPendingOverride(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'user', userId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const featureFlagsQuery = useQuery({
    queryKey: ['admin', 'user', userId, 'feature-flags'],
    queryFn: () =>
      apiJson<UserFeatureFlagsResponse>(
        `/v1/admin/users/${encodeURIComponent(userId)}/feature-flags`,
      ),
  });

  const featureFlagMutation = useMutation({
    mutationFn: ({ key, allowlisted }: { key: string; allowlisted: boolean }) =>
      apiJson<UserFeatureFlagItem>(
        `/v1/admin/users/${encodeURIComponent(userId)}/feature-flags/${encodeURIComponent(key)}?allowlisted=${allowlisted ? 'true' : 'false'}`,
        { method: 'PUT' },
      ),
    onSuccess: () => {
      setPendingFlag(null);
      void featureFlagsQuery.refetch();
      void qc.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
    },
  });

  const clearStreakMutation = useMutation({
    mutationFn: () =>
      apiJson<void>(
        `/v1/admin/users/${encodeURIComponent(userId)}/streak`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      setConfirmClearStreak(false);
      void qc.invalidateQueries({ queryKey: ['admin', 'user', userId] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (error) {
    return (
      <p className="text-sm text-red-600">
        {(error as Error).message}
        <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
          Retry
        </button>
      </p>
    );
  }
  if (!data) return null;
  const u = data.user;
  const hasPremiumAccess = u.subscribed || u.subscription_override;
  const flagItems = featureFlagsQuery.data?.items ?? [];
  const showFlagsTab = flagItems.length > 0 || featureFlagsQuery.isError;
  const tabs = USER_DETAIL_TABS.filter((item) => item.id !== 'flags' || showFlagsTab);
  const activeTab: UserDetailTab = tabs.some((item) => item.id === tab) ? tab : 'profile';

  return (
    <div className={activeTab === 'village' ? 'max-w-6xl' : 'max-w-4xl'}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">
            {u.display_name ?? 'User'}
          </h1>
          <p className="mt-1 text-sm text-nova-muted-foreground">
            {hasPremiumAccess ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                  Premium access
                  {u.subscription_override ? ' (override)' : u.subscribed ? ' (RevenueCat)' : ''}
                </span>
              </span>
            ) : (
              <span>No premium access</span>
            )}
          </p>
        </div>
        <Button variant="secondary" onClick={() => void navigate({ to: '/admin/users' })}>
          Back to list
        </Button>
      </div>

      <div
        role="tablist"
        aria-label="User sections"
        className="mt-6 flex flex-wrap gap-x-1 border-b border-nova-border"
        onKeyDown={(event) => onTabListKeyDown(event, tabs, activeTab, setTab)}
      >
        {tabs.map((item) => {
          const selected = activeTab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`user-detail-tab-${item.id}`}
              aria-selected={selected}
              aria-controls="user-detail-panel"
              tabIndex={selected ? 0 : -1}
              onClick={() => setTab(item.id)}
              className={`-mb-px border-b-2 px-3 py-2.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-nova-ring/40 ${
                selected
                  ? 'border-nova-primary text-nova-primary'
                  : 'border-transparent text-nova-muted-foreground hover:border-nova-input hover:text-nova-foreground'
              }`}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      <div
        id="user-detail-panel"
        role="tabpanel"
        aria-labelledby={`user-detail-tab-${activeTab}`}
        className="pt-5"
      >
        {activeTab === 'profile' ? (
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <dt className="text-nova-muted-foreground">id</dt>
            <dd className="break-all font-mono text-xs">{u.id}</dd>
            <dt className="text-nova-muted-foreground">display_name</dt>
            <dd>{u.display_name ?? '—'}</dd>
            <dt className="text-nova-muted-foreground">email</dt>
            <dd>{u.email ?? '—'}</dd>
            <dt className="text-nova-muted-foreground">clerk_id</dt>
            <dd className="break-all font-mono text-xs">{u.clerk_id ?? '—'}</dd>
            <dt className="text-nova-muted-foreground">created_at</dt>
            <dd>{formatAdminDateTime(u.created_at)}</dd>
            <dt className="text-nova-muted-foreground">updated_at</dt>
            <dd>{formatAdminDateTime(u.updated_at)}</dd>
            <dt className="text-nova-muted-foreground">deleted_at</dt>
            <dd>{u.deleted_at ? formatAdminDateTime(u.deleted_at) : '—'}</dd>
            <dt className="text-nova-muted-foreground">linked devices</dt>
            <dd>{formatInt(u.device_count)}</dd>
            <dt className="text-nova-muted-foreground">RevenueCat subscribed</dt>
            <dd>{u.subscribed ? 'Yes' : 'No'}</dd>
          </dl>
        ) : null}

        {activeTab === 'subscription' ? (
          <div>
            <p className="mb-4 text-sm text-nova-muted-foreground">
              Grant premium access without a RevenueCat subscription (support comps, testing).
              RevenueCat status is unchanged.
            </p>
            <label className="inline-flex min-h-8 cursor-pointer select-none items-center gap-3 py-1">
              <input
                type="checkbox"
                role="switch"
                aria-checked={
                  pendingOverride ? pendingOverride.next : u.subscription_override
                }
                checked={
                  pendingOverride ? pendingOverride.next : u.subscription_override
                }
                disabled={overrideMutation.isPending}
                onChange={(e) => {
                  const next = e.target.checked;
                  setPendingOverride({
                    next,
                    title: next ? 'Grant override?' : 'Remove override?',
                    message: next
                      ? 'Grant subscription override for this user?'
                      : 'Remove subscription override for this user?',
                  });
                }}
                className="h-5 w-5 rounded border-nova-input text-nova-primary focus:ring-nova-ring disabled:opacity-50"
              />
              <span className="text-sm font-medium text-nova-foreground">
                {overrideMutation.isPending
                  ? 'Saving…'
                  : (pendingOverride ? pendingOverride.next : u.subscription_override)
                    ? 'Override enabled'
                    : 'Override disabled'}
              </span>
            </label>
            {overrideMutation.isError ? (
              <p className="mt-2 text-sm text-red-600">
                {(overrideMutation.error as Error).message || 'Failed to save. Try again.'}
              </p>
            ) : null}
          </div>
        ) : null}

        {activeTab === 'flags' ? (
          <div>
            {featureFlagsQuery.isError ? (
              <p className="text-sm text-red-600">
                {(featureFlagsQuery.error as Error).message ||
                  'Failed to load feature flags.'}
                <button
                  type="button"
                  className="ml-2 underline"
                  onClick={() => void featureFlagsQuery.refetch()}
                >
                  Retry
                </button>
              </p>
            ) : (
              <>
                <p className="mb-4 text-sm text-nova-muted-foreground">
                  Allowlist-only flags for this user. The flag must be enabled globally in
                  Feature flags for allowlisted users to receive it in the app.
                </p>
                <ul className="space-y-3">
                  {flagItems.map((flag) => {
                    const pendingForFlag =
                      pendingFlag?.key === flag.key ? pendingFlag.next : flag.allowlisted;
                    return (
                      <li key={flag.key}>
                        <label className="inline-flex min-h-8 cursor-pointer select-none items-center gap-3 py-1">
                          <input
                            type="checkbox"
                            role="switch"
                            aria-checked={pendingForFlag}
                            checked={pendingForFlag}
                            disabled={featureFlagMutation.isPending || !flag.enabled}
                            onChange={(e) => {
                              const next = e.target.checked;
                              setPendingFlag({
                                key: flag.key,
                                next,
                                title: next ? 'Add to allowlist?' : 'Remove from allowlist?',
                                message: next
                                  ? `Add this user to the ${flag.key} allowlist?`
                                  : `Remove this user from the ${flag.key} allowlist?`,
                              });
                            }}
                            className="h-5 w-5 rounded border-nova-input text-nova-primary focus:ring-nova-ring disabled:opacity-50"
                          />
                          <span className="text-sm font-medium text-nova-foreground">
                            {flag.key}
                            {!flag.enabled ? ' (flag disabled globally)' : ''}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
                {featureFlagMutation.isError ? (
                  <p className="mt-2 text-sm text-red-600">
                    {(featureFlagMutation.error as Error).message ||
                      'Failed to save. Try again.'}
                  </p>
                ) : null}
              </>
            )}
          </div>
        ) : null}

        {activeTab === 'streak' ? (
          <div>
            <p className="mb-4 text-sm text-nova-muted-foreground">
              Daily practice streak for this account. Clearing resets the streak
              profile and removes today&apos;s flashcard views/completions so you can
              re-earn the day from zero. Lifetime game-run history is kept.
            </p>
            {data.streak ? (
              <dl className="mb-4 grid gap-2 text-sm sm:grid-cols-2">
                <dt className="text-nova-muted-foreground">Current streak</dt>
                <dd>{formatInt(data.streak.current_streak)}</dd>
                <dt className="text-nova-muted-foreground">Longest streak</dt>
                <dd>{formatInt(data.streak.longest_streak)}</dd>
                <dt className="text-nova-muted-foreground">Qualified today</dt>
                <dd>{data.streak.qualified_today ? 'Yes' : 'No'}</dd>
                <dt className="text-nova-muted-foreground">Last qualified</dt>
                <dd>{data.streak.last_qualified_local_date ?? '—'}</dd>
                <dt className="text-nova-muted-foreground">Freezes remaining</dt>
                <dd>{formatInt(data.streak.freezes_remaining)}</dd>
                <dt className="text-nova-muted-foreground">Timezone</dt>
                <dd>{data.streak.timezone}</dd>
              </dl>
            ) : (
              <p className="mb-4 text-sm text-nova-muted-foreground">No streak profile yet.</p>
            )}
            <Button
              variant="danger"
              size="sm"
              disabled={clearStreakMutation.isPending}
              onClick={() => {
                clearStreakMutation.reset();
                setConfirmClearStreak(true);
              }}
            >
              Clear streak
            </Button>
            {clearStreakMutation.isError ? (
              <p className="mt-2 text-sm text-red-600">
                {(clearStreakMutation.error as Error).message ||
                  'Failed to clear streak. Try again.'}
              </p>
            ) : null}
          </div>
        ) : null}

        {activeTab === 'activity' ? (
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <dt className="text-nova-muted-foreground">Flashcards swiped</dt>
            <dd>{formatInt(data.stats.flashcards_swiped)}</dd>
            <dt className="text-nova-muted-foreground">Game runs played</dt>
            <dd>{formatInt(data.stats.game_runs_played)}</dd>
          </dl>
        ) : null}

        {activeTab === 'village' ? <UserVillagePanel userId={userId} /> : null}

        {activeTab === 'devices' ? (
          data.devices.length === 0 ? (
            <p className="text-sm text-nova-muted-foreground">No devices linked to this account.</p>
          ) : (
            <div className="space-y-3">
              {data.devices.map((d) => (
                <DeviceDetailsCard key={d.installation_id} device={d} />
              ))}
            </div>
          )
        ) : null}

        {activeTab === 'push' ? <DeviceTestPushPanel devices={data.devices} /> : null}
      </div>

      <ConfirmDialog
        open={pendingOverride != null}
        title={pendingOverride?.title ?? ''}
        message={pendingOverride?.message ?? ''}
        confirmLabel={pendingOverride?.next ? 'Grant' : 'Remove'}
        confirmVariant={pendingOverride?.next ? 'primary' : 'danger'}
        loading={overrideMutation.isPending}
        onCancel={() => {
          if (!overrideMutation.isPending) {
            setPendingOverride(null);
            overrideMutation.reset();
          }
        }}
        onConfirm={() => {
          if (pendingOverride) void overrideMutation.mutate(pendingOverride.next);
        }}
      />

      <ConfirmDialog
        open={pendingFlag != null}
        title={pendingFlag?.title ?? ''}
        message={pendingFlag?.message ?? ''}
        confirmLabel={pendingFlag?.next ? 'Add' : 'Remove'}
        confirmVariant={pendingFlag?.next ? 'primary' : 'danger'}
        loading={featureFlagMutation.isPending}
        onCancel={() => {
          if (!featureFlagMutation.isPending) {
            setPendingFlag(null);
            featureFlagMutation.reset();
          }
        }}
        onConfirm={() => {
          if (pendingFlag) {
            void featureFlagMutation.mutate({
              key: pendingFlag.key,
              allowlisted: pendingFlag.next,
            });
          }
        }}
      />

      <ConfirmDialog
        open={confirmClearStreak}
        title="Clear streak?"
        message="Resets current/longest streak and freezes, and deletes today's flashcard views and set completions for this account so the day can be re-earned. Lifetime game runs are kept."
        confirmLabel="Clear streak"
        confirmVariant="danger"
        loading={clearStreakMutation.isPending}
        onCancel={() => {
          if (!clearStreakMutation.isPending) {
            setConfirmClearStreak(false);
            clearStreakMutation.reset();
          }
        }}
        onConfirm={() => {
          void clearStreakMutation.mutate();
        }}
      />
    </div>
  );
}

function onTabListKeyDown(
  event: KeyboardEvent<HTMLDivElement>,
  tabs: readonly { id: UserDetailTab }[],
  activeTab: UserDetailTab,
  setTab: (tab: UserDetailTab) => void,
) {
  const index = tabs.findIndex((item) => item.id === activeTab);
  if (index < 0) return;
  let next = index;
  if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
  else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
  else if (event.key === 'Home') next = 0;
  else if (event.key === 'End') next = tabs.length - 1;
  else return;
  event.preventDefault();
  const id = tabs[next].id;
  setTab(id);
  document.getElementById(`user-detail-tab-${id}`)?.focus();
}
